import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /*
   * Provenance fix, 27 Sep 2026: "the dropdown disappears when users try to select an option".
   * Root causes in the stock code:
   *  1. pointerleave on the top-level <li> closed the panel instantly, with no grace period. The panel
   *     is revealed by a 360 ms clip-path transition and clip-path also clips hit testing, so while it
   *     is still growing (and at the 1px seam between the link row and the panel) the pointer is
   *     briefly over nothing that belongs to the <li>; one frame there closed the menu.
   *  2. Clicking the caret after hover had opened the menu read aria-expanded="true" and closed it.
   *  3. Touch and pen: pointerenter opened the menu, then the lifted finger fired pointerleave
   *     (instant close) and the click followed the top-level link.
   *  4. Leaflet maps (z-index 400 to 1000, no stacking context) painted over the open panel on map
   *     pages, so moving onto the options landed on the map and closed the menu (CSS fix).
   * Now: 300 ms close delay for mouse hover, re-entry cancels it; a click or tap (caret, touch tap on a
   * parent link, Enter or Space) pins the panel open until an outside click, Escape or a second
   * click; the first tap on a parent link opens, the second follows it; ArrowDown opens and focuses
   * the first option.
   */
  static CLOSE_DELAY_MS = 300;

  /** V43 C-1: how many times a pending close may re-arm while the pointer is inside the panel area. */
  static MAX_CLOSE_REARMS = 4;

  /** Hover intent: delay before an open menu switches to a neighbouring top-level item. */
  static SWITCH_DELAY_MS = 90; // V65: was 120, felt laggy

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #switchTimer;

  /**
   * Provenance fix 2, 10 Oct 2026: the text mega menu lays its options out from the left edge of the
   * page, so the options for Learn or About sat about 750 px left of their link and the pointer had to
   * cross other links to reach them. On desktop the option list now slides under its own link,
   * clamped so it never runs past the right edge of the page width.
   * @param {Element} listItem
   * @param {HTMLElement} submenu
   */
  #alignSubmenu(listItem, submenu) {
    try {
      const ul = /** @type {HTMLElement | null} */ (submenu.querySelector('.mega-menu__list'));
      // V43 C-8: clear any old desktop offset before the width check, so a window narrowed below
      // 990 px does not keep the last translateX.
      if (ul) ul.style.transform = '';
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      const grid = submenu.querySelector('.mega-menu__grid');
      if (!ul || !grid || submenu.querySelector('.mega-menu__content-list, .mega-menu__media')) return;
      ul.style.transform = '';
      const g = grid.getBoundingClientRect();
      let right = g.left;
      for (const col of Array.from(ul.children)) {
        const r = col.getBoundingClientRect();
        if (r.width) right = Math.max(right, r.right);
      }
      const used = right - g.left;
      const trigger = listItem.getBoundingClientRect().left;
      const offset = Math.max(0, Math.min(trigger - g.left, g.width - used));
      ul.style.transform = offset ? `translateX(${Math.round(offset)}px)` : '';
    } catch (e) {}
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** Opened by click, tap or keyboard: ignore pointerleave until an outside click or Escape. */
  #pinned = false;

  /** Pointer type of the most recent pointerdown anywhere in the document. */
  #lastPointerType = 'mouse';

  /** @param {Event} event */
  static isTouchLike(event) {
    const type = event && 'pointerType' in event ? /** @type {PointerEvent} */ (event).pointerType : '';
    return type === 'touch' || type === 'pen';
  }

  #cancelClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  /** @param {PointerEvent} event */
  #onDocPointerDown = (event) => {
    this.#lastPointerType = event.pointerType || 'mouse';
    const item = this.#state.activeItem;
    if (!item) return;
    const path = event.composedPath ? event.composedPath() : [];
    if (path.includes(this)) return;
    // Outside click or tap closes an open panel, pinned or not.
    this.#deactivate(item, { force: true });
  };

  /** @param {MouseEvent} event */
  #onClickCapture = (event) => {
    if (this.#lastPointerType !== 'touch' && this.#lastPointerType !== 'pen') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a.menu-list__link');
    if (!(link instanceof HTMLElement) || !findSubmenu(link)) return;
    if (this.#state.activeItem === link) return; // second tap follows the link
    event.preventDefault();
    this.activate(/** @type {any} */ ({ target: link, type: 'click' }));
    this.#pinned = true;
  };

  /**
   * V43 C-1: the pointer left the browser window. #lastPointer only updates on body pointermove, so
   * without this the last in-band position froze and a hover-opened menu stayed open. Forget the
   * position and close after the usual delay (a pinned panel stays open).
   * @param {MouseEvent} event
   */
  #onDocMouseOut = (event) => {
    if (event.relatedTarget || !this.#state.activeItem) return;
    this.#lastPointer.x = -1;
    this.#lastPointer.y = -1;
    if (this.#pinned) return;
    this.#scheduleClose(this.#state.activeItem);
  };

  /** @param {KeyboardEvent} event */
  #onDocKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    // Focus inside the menu is handled by #onKeydown.
    if (this.contains(document.activeElement)) return;
    this.#deactivate(this.#state.activeItem, { force: true });
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('click', this.#onClickCapture, true);
    document.addEventListener('pointerdown', this.#onDocPointerDown, true);
    document.addEventListener('keydown', this.#onDocKeydown);
    document.addEventListener('mouseout', this.#onDocMouseOut); // V43 C-1
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('click', this.#onClickCapture, true);
    document.removeEventListener('pointerdown', this.#onDocPointerDown, true);
    document.removeEventListener('keydown', this.#onDocKeydown);
    document.removeEventListener('mouseout', this.#onDocMouseOut); // V43 C-1
    this.#cancelClose();
    // V43 C-2: a pending hover-intent switch must not reopen a menu after the element is gone.
    clearTimeout(this.#switchTimer);
    this.#switchTimer = undefined;
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = (/** @type {PointerEvent} */ event) => {
    if (this.#pinned || HeaderMenu.isTouchLike(event)) return;
    this.#scheduleClose(this.#state.activeItem);
  };

  /**
   * Escape closes the open submenu and restores focus to its disclosure control.
   * ArrowDown on a top-level link or caret opens its submenu and focuses the first option.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === 'ArrowDown') {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('.menu-list__trigger')) return;
      const item = findMenuItem(target);
      const submenu = findSubmenu(item);
      if (!item || !submenu) return;
      event.preventDefault();
      if (this.#state.activeItem !== item) this.activate(/** @type {any} */ ({ target: item, type: 'keydown' }));
      this.#pinned = true;
      requestAnimationFrame(() => {
        const first = submenu.querySelector('a[href], button:not([disabled])');
        if (first instanceof HTMLElement) first.focus();
      });
      return;
    }

    if (event.key !== 'Escape') return;
    const item = this.#state.activeItem;
    if (!item) return;

    const control = this.#focusTargetAfterClose(item);
    this.#deactivate(item, { force: true });
    control.focus();
  };

  /**
   * Toggle a submenu from its dedicated disclosure button (click / Enter / Space).
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const item = findMenuItem(button);
    if (!item) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    // Opened by hover and now clicked: pin it open instead of closing it under the pointer.
    // V43 D-10: only for pointer clicks. Keyboard Enter or Space arrives as a click with detail 0 and
    // must close an expanded caret, as its aria-expanded state promises.
    if (isExpanded && !this.#pinned && event.detail !== 0) {
      this.#cancelClose();
      this.#pinned = true;
      return;
    }
    if (isExpanded) {
      // Close whatever is currently active. In the overflow panel the initially
      // selected item is tracked as `activeOverflowItem` while the More trigger is
      // the `activeItem`, so deactivating `item` directly would no-op; deactivate
      // the active item instead.
      this.#deactivate(this.#state.activeItem ?? item, { force: true });
      // Closing the overflow panel hides the focused disclosure, so return focus
      // to the visible More trigger; a top-level disclosure stays put.
      const focusTarget = this.#focusTargetAfterClose(item);
      if (focusTarget !== button) focusTarget.focus();
    } else {
      this.activate(event);
      this.#pinned = true;
    }
  };

  /**
   * The control that should receive focus after a submenu closes. Overflow items
   * live in a panel that is hidden on close, so focus returns to the always-visible
   * More trigger instead of a now-inert control inside the panel.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    if (slot === 'overflow' || slot === 'more') {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) return moreButton;
    }
    return this.#expandableFor(item);
  }

  /**
   * Return the element that carries the `aria-expanded` state for a menu item.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #expandableFor(item) {
    const listItem = item.closest('.menu-list__list-item');
    const disclosure = listItem?.querySelector('[ref="disclosure[]"]');
    return disclosure instanceof HTMLElement ? disclosure : item;
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // Touch and pen have no hover. Their pointerenter must not open the menu; a tap does (#onClickCapture).
    if (event.type === 'pointerenter' && HeaderMenu.isTouchLike(event)) return;

    const item = findMenuItem(event.target);

    if (item && item == this.#state.activeItem) {
      // Pointer came back before the close delay ran out.
      this.#cancelClose();
      clearTimeout(this.#switchTimer);
      return;
    }
    if (!item) return;

    /*
     * Provenance fix 2, 10 Oct 2026: hover intent. With a panel already open, a pointer travelling
     * diagonally toward an option crossed a neighbouring top-level link and the menu switched to that
     * link (the option the user wanted vanished). Switching now waits 120 ms (V43 C-2 comment fix) and only happens if the
     * pointer is still on the new link. The first open, focus, keyboard and taps stay instant.
     */
    if (event.type === 'pointerenter' && this.#state.activeItem && this.#state.activeItem !== item) {
      clearTimeout(this.#switchTimer);
      const intentLi = item.closest('.menu-list__list-item') || item;
      const intentTarget = event.target;
      this.#switchTimer = setTimeout(() => {
        this.#switchTimer = undefined;
        // Switch if the pointer is still on the new link, or has gone straight down from it into the
        // panel (its options now sit under it). Only a pointer that has moved away from the link's
        // column keeps the old menu.
        // V43 C-2: the column band now has a lower bound (the open panel's top + 40 px). Without it a
        // pointer anywhere deep inside the open panel, below a neighbouring link, switched the menu
        // and the option being reached vanished.
        const r = intentLi.getBoundingClientRect();
        const { x, y } = this.#lastPointer;
        const activeSub = findSubmenu(this.#state.activeItem);
        const activeInner = activeSub?.querySelector('.menu-list__submenu-inner');
        const ar = activeInner ? activeInner.getBoundingClientRect() : null;
        const bandBottom = (ar && ar.height ? Math.max(ar.top, r.bottom) : r.bottom) + 40;
        const underIt = x >= r.left - 4 && x <= r.right + 4 && y >= r.top && y <= bandBottom;
        if (intentLi.matches(':hover') || underIt) this.activate(/** @type {any} */ ({ target: intentTarget, type: 'pn-intent' }));
      }, HeaderMenu.SWITCH_DELAY_MS);
      return;
    }
    clearTimeout(this.#switchTimer);

    this.#cancelClose();
    // Hovering a different top-level item switches to it and drops any pin.
    this.#pinned = false;

    // Derive the slot context from the list item rather than `event.target`.
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const isDefaultSlot = slot === '';
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      this.#expandableFor(previouslyActiveItem).ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) previousSubmenu.inert = true;
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      this.#expandableFor(previouslyActiveOverflowItem).ariaExpanded = 'false';
      const previousOverflowSubmenu = findSubmenu(previouslyActiveOverflowItem);
      if (previousOverflowSubmenu) previousOverflowSubmenu.inert = true;
    }

    // V65: switching between open menus swaps content instantly (no fade from blank); a first open still fades in.
    if (previouslyActiveItem) this.dataset.pnSwitch = '';

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    this.#expandableFor(item).ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      this.#expandableFor(overflowItem).ariaExpanded = 'true';
      // The More trigger auto-opens the first overflow item's submenu, so expose it.
      const activeOverflowSubmenu = findSubmenu(overflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = false;
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Expose the open submenu to focus and assistive tech. Collapsed submenus stay
      // inert so their contents cannot be reached (visibility:hidden alone can be
      // overridden by descendants that re-assert visibility).
      submenu.inert = false;
      // V65: align before the first painted frame (was in requestAnimationFrame, so options jumped sideways after showing).
      if (isDefaultSlot && listItem) this.#alignSubmenu(listItem, submenu);

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (event.type === 'pointerleave') {
      // Touch and pen fire pointerleave as the finger lifts: never a reason to close.
      // A pinned panel stays open until an outside click, Escape or a second click.
      if (HeaderMenu.isTouchLike(event) || this.#pinned) return;
      this.#scheduleClose(this.#state.activeItem);
      return;
    }

    // Focus dropped to <body> (a click on panel whitespace): not a reason to close while pinned or hovered.
    // Clicks outside the menu are handled by #onDocPointerDown.
    if (!event.relatedTarget && (this.#pinned || event.target.matches(':hover'))) return;

    const menu = findSubmenu(this.#state.activeItem);
    // Keep the submenu open while focus moves between the link, its disclosure
    // button, and the submenu contents, i.e. anywhere inside the same list item.
    const isMovingWithinItem =
      event.relatedTarget instanceof Node &&
      event.target instanceof Element &&
      event.target.contains(event.relatedTarget);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinItem || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Close after CLOSE_DELAY_MS unless the pointer is back over the item, its panel or the overflow panel.
   * @param {HTMLElement | null} item
   * @param {number} [tries] V43 C-1: re-arms used so far while the pointer was in the panel area.
   */
  #scheduleClose(item, tries = 0) {
    if (!item) return;
    this.#cancelClose();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      if (this.#pinned || item !== this.#state.activeItem) return;
      const listItem = item.closest('.menu-list__list-item');
      const submenu = findSubmenu(item);
      if (listItem?.matches(':hover') || submenu?.matches(':hover')) return;
      // Provenance fix 3, 10 Oct 2026: moving straight down to a lower option while the panel was still
      // revealing (clip-path animation, which also hides it from hit testing) put the pointer over the
      // page underneath, and the menu closed under the cursor. If the pointer is inside the panel's
      // laid-out area (or the seam between the link and the panel), keep it open and check again.
      // V43 C-1: capped at MAX_CLOSE_REARMS (4 x 300 ms covers the 360 ms reveal) so it cannot loop forever.
      if (tries < HeaderMenu.MAX_CLOSE_REARMS && submenu && listItem && this.#pointerInPanel(listItem, submenu)) {
        this.#scheduleClose(item, tries + 1);
        return;
      }
      this.#deactivate(item);
    }, HeaderMenu.CLOSE_DELAY_MS);
  }

  /**
   * @param {Element} listItem
   * @param {Element} submenu
   */
  #pointerInPanel(listItem, submenu) {
    try {
      const { x, y } = this.#lastPointer;
      const li = listItem.getBoundingClientRect();
      // V43 C-1: the old box was the full-width submenu element from the link's top edge down, so the
      // whole header row (logo, search, account, cart) counted as "inside" and the menu never closed.
      // Now: (a) the seam column directly under the link, from li.bottom - 2 down to the panel, and
      // (b) the visible panel content rect (.menu-list__submenu-inner), never above li.bottom - 2.
      const inner = submenu.querySelector('.menu-list__submenu-inner');
      const ir = inner ? inner.getBoundingClientRect() : null;
      if (!ir || !ir.width || !ir.height) return false;
      const top = Math.max(ir.top, li.bottom - 2);
      const inSeam = x >= li.left && x <= li.right && y >= li.bottom - 2 && y <= top + 2;
      const inPanel = x >= ir.left && x <= ir.right && y >= top && y <= ir.bottom;
      return inSeam || inPanel;
    } catch (e) {
      return false;
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered,
    // unless the close was explicitly requested (Escape or the disclosure toggle),
    // which must close regardless of pointer position.
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.#cancelClose();
    this.#pinned = false;
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    // V43 C-2: an outside click or Escape within the switch delay must not reopen the neighbour.
    clearTimeout(this.#switchTimer);
    this.#switchTimer = undefined;

    delete this.dataset.pnSwitch; // V65
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    this.#expandableFor(item).ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      this.#expandableFor(activeOverflowItem).ariaExpanded = 'false';
      const activeOverflowSubmenu = findSubmenu(activeOverflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = true;
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // `element` may be the list item itself (hover/focus routed via the `<li>`) or a
  // descendant such as the disclosure button (click routed via the button).
  const listItem = element.closest('.menu-list__list-item') ?? element;
  return listItem.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
